import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { useQuery } from '@tanstack/react-query';
import { vehicleService } from '../../application/VehicleService';
import { VehicleActionType } from '../../context/vehicle/actions';
import { useVehicleContext } from '../../context/vehicle/context';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseSearchVehiclesProps {
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: string;
  searchText?: string;
}

type UseRetrieveVehiclesResponse = ServiceResponse<Function, PaginatedResult<Vehicle>>;

// Hooks act as 'Adapter' layer.
export const useSearchVehicles = ({
  offset,
  limit,
  sortBy,
  sortDirection,
  searchText,
}: UseSearchVehiclesProps): UseRetrieveVehiclesResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: activeProject } = useRetrieveActiveProject();
  const { vehicleDispatch, vehicleState } = useVehicleContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const { isLoading, isRefetching, refetch, isError } = useQuery(
    [`planner_vehicles`, activeProject!.id, offset, limit, searchText, sortBy, sortDirection],
    () =>
      vehicleService.searchVehicles({
        project: activeProject!,
        offset,
        limit,
        searchText,
        sortBy: sortBy,
        sortDirection: sortDirection,
      }),
    {
      enabled: !!activeProject,
      refetchOnWindowFocus: false,
      onSuccess: (vehicles: PaginatedResult<Vehicle>) => {
        vehicleDispatch({
          type: VehicleActionType.SEARCH_VEHICLES_SUCCESS,
          payload: { vehicles },
        });
      },
      onError: (err: any) => {
        console.info(`Planner retrieve vehicles Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: true,
    isLoading: isRefetching || isLoading,
    data: vehicleState.vehicles,
    run: refetch,
  };
};
